<template>
  <div>
    <ViewHeader>
      <CButton
        color="secondary"
        class="ra-button"
        data-cy="create-ticket-link"
        @click="() => $downloadFile({
          url: USER_ENDPOINTS.export_members,
          query: $refs.members_table.tableQuery
        })"
      >
        Export members
      </CButton>
      <CButton
        color="primary"
        class="ra-button"
        to="/members/create"
      >
        <Icon name="plus" class="mr-1"/>
        Create
      </CButton>
    </ViewHeader>
    <div
      v-if="numberOfMembers === 0"
      class="d-flex align-items-center justify-content-center"
    >
      <div class="d-flex flex-column align-items-center justify-content-center">
        <span class="fa-2x mb-5 mt-5">No members exist</span>
        <img class="w-100" src="/img/undraw/no_members.svg" />
      </div>
    </div>
    <div v-if="roleOptions.length && financialRoles.length">
      <tableComponent
        v-show="numberOfMembers !== 0"
        sort-exclude
        :default-per-page="25"
        :filterExcludeColumns="['actions', 'role', 'financial_role', 'email_notification']"
        :sortExcludeColumns="['role', 'financial_role']"
        :url="USER_ENDPOINTS.list"
        :init-filters="getInitFilters()"
        :force-filter="true"
        :columns="columns"
        :headings="{
          last_update_str: 'Last update'
        }"
        ref="members_table"
        :key="refreshTable"
      >
        <template #filter__email_notification>
          <InputComponent
            class="attached-file-filter"
            :formField="availableEmailNotification"
            @select="selectEmailNotification"
            @remove="() => {}"
            @dropdownClosed="this.refreshTable = !this.refreshTable"
          />
        </template>
        <template #h__role>
          <div
            class="
              d-flex
              justify-content-center
              align-items-center
              p-1
              w-100
              text-center
              roles-secondary
            "
          >
            <div />
            Role
          </div>
        </template>
        <template #filter__role>
          <div class="role-options roles-secondary w-100">
            <b v-for="role in roleOptions">{{ role.value }}</b>
          </div>
        </template>
        <template #filter__financial_role>
          <div
            class="role-options roles-secondary w-100"
          >
            <b v-for="role in financialRoles">{{ role.value }}</b>
          </div>
        </template>
        <template #h__financial_role>
          <div
            class="
              d-flex
              justify-content-center
              align-items-center
              p-1
              w-100
              text-center
              roles-secondary
            "
          >
            <div />
            <div>Restricted scope</div>
          </div>
        </template>
        <template #role="{ row }">
          <div class="roles roles-secondary">
            <CInputCheckboxGroup
              :options="roleOptions"
              :checked="row.roles"
              class="role-options"
              custom
              inline
              disabled
            />
          </div>
        </template>
        <template #financial_role="{ row }">
          <div
            class="roles roles-secondary"
          >
            <CInputCheckboxGroup
              :options="financialRoles"
              :checked="row.roles.includes('Restricted scope') ? row.financial_role : []"
              class="role-options"
              custom
              inline
              disabled
            />
          </div>
        </template>
        <template #email_notification="{ row }">
          <div v-if="row.email_notification">Yes</div>
          <div v-else>No</div>
        </template>
        <template #actions="{ row }">
          <div class="d-flex gap-2">
            <CLink :to="`/members/${row._id}?edit=1`">
              <CButton
                class="ra-icon-button"
                v-c-tooltip="'Edit member'"
              >
                <Icon name="pencil"/>
              </CButton>
            </CLink>
            <CButton
              class="ra-icon-button"
              v-c-tooltip="'Delete member'"
              @click="() => deleteUser(row)"
            >
              <Icon name="trash"/>
            </CButton>
          </div>
        </template>
      </tableComponent>
    </div>
  </div>
</template>

<script>
import { UsersService, USER_ENDPOINTS } from "@/services/users";
import {request} from "@/utils/request";
import InputComponent from "@/components/InputComponent";

export default {
  name: "MembersList",
  components: {
    InputComponent,
  },
  data() {
    return {
      USER_ENDPOINTS,
      roleOptions: [],
      financialRoles: [],
      numberOfMembers: 0,
      refreshTable: false,
      columns: [
        "firstname",
        "lastname",
        "email",
        "last_update_str",
        "role",
        "financial_role",
        "email_notification",
        "actions",
      ],
      availableEmailNotification: {
        hideLabel: true,
        name: "attached_file",
        type: "select",
        searchable: true,
        value: '',
        options: [
          {value: '', label: "All"},
          {value: true, label: "Yes"},
          {value: false, label: "No"},
        ],
      },
    };
  },
  async mounted() {
    const {data: memNum} = await request({url: USER_ENDPOINTS.list})
    this.numberOfMembers = memNum.length
    const roles = await UsersService.getRoles();
    this.roleOptions = roles.role.filter(role => !role.includes('user')).map((role) => {
      return { label: null, value: role };
    });
    this.financialRoles = roles.financial_role.map((role) => {
      return { label: null, value: role };
    });
  },
  methods: {
    async deleteUser(row) {
      if (!(await confirm(`Are you sure you want to delete ${row.email}?`, {okText: "Delete this user", cancelText: "Cancel"}))) {
        return;
      }
      await UsersService.deleteUser(row._id);
      this.refreshTable = !this.refreshTable
    },
    selectEmailNotification(item) {
      this.availableEmailNotification.value = item.value;
      this.refreshTable = !this.refreshTable
    },
    tableEmailNotificationFilters() {
      if (this.availableEmailNotification.value === '') return {};
      return { search_email_notification: this.availableEmailNotification.value };
    },
    getInitFilters() {
      return {
        ...this.tableEmailNotificationFilters(),
      };
    },
  },
};
</script>

<style lang="scss">

.role-options {
  &,
  .form-inline {
    display: grid !important;
    grid-auto-columns: minmax(110px, 1fr);
    grid-auto-flow: column;
    align-content: center;
  }

  b {
    text-align: center;
  }

  .custom-control {
    padding-left: 1rem !important;
    margin-right: 0;
  }

  .custom-control-label {
    cursor: pointer;

    &:after,
    &:before {
      top: -7px !important;
    }
  }
}
</style>
